import React, { useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  activeOfferFor,
  homepageContent,
  offersContent,
  products,
  siteContent,
  defaultVariantId,
  toOrderProduct
} from '../../data/products';
import {
  earliestOrderDate,
  formatINR,
  normalizeIndianPhone
} from '../../utils/commerce';
import { buildSingleOrderMessage, buildWhatsAppUrl } from '../../utils/whatsapp';
import { saveOrderEnquiry } from '../../utils/orderEnquiry';
import Icon from '../Icon/Icon';
import './Order.css';

const Order = () => {
  const [ref, inView] = useInView({ threshold: 0.15, triggerOnce: true });
  const orderableProducts = products.filter((product) => product.active !== false && product.availability !== 'sold-out');
  const defaultProduct = orderableProducts[0];
  const content = homepageContent.order;
  const studentOffer = activeOfferFor(offersContent, 'order');
  const instagramUrl = siteContent.contact.instagramUrl;
  const [order, setOrder] = useState(() => ({
    name: '',
    phone: '',
    itemId: defaultProduct?.id ? String(defaultProduct.id) : '',
    variantId: defaultVariantId(defaultProduct),
    quantity: 1,
    fulfillment: 'delivery',
    area: '',
    address: '',
    date: '',
    time: siteContent.ordering.timeSlots[0] || 'Flexible',
    eggless: false,
    notes: ''
  }));
  const [errors, setErrors] = useState({});
  const [enquiryStatus, setEnquiryStatus] = useState('');
  const [sent, setSent] = useState(false);
  const [whatsappFallback, setWhatsappFallback] = useState('');
  const formRef = useRef(null);

  const selectedProduct = products.find((product) => product.id === Number(order.itemId));
  const selectedOrderProduct = toOrderProduct(selectedProduct, order.variantId);
  const quantity = Number(order.quantity) || 1;
  const earliestDate = earliestOrderDate(selectedProduct ? [selectedProduct] : []);

  const updateOrder = (event) => {
    const { name, value, type, checked } = event.target;
    setOrder((current) => {
      if (name === 'itemId') {
        const product = products.find((item) => item.id === Number(value));
        return { ...current, itemId: value, variantId: defaultVariantId(product) };
      }
      return { ...current, [name]: type === 'checkbox' ? checked : value };
    });
    setErrors((current) => ({ ...current, [name]: undefined }));
    setSent(false);
    setWhatsappFallback('');
  };

  const stepQty = (delta) => {
    setOrder((current) => ({
      ...current,
      quantity: Math.min(siteContent.ordering.maxQuantity, Math.max(1, Number(current.quantity) + delta))
    }));
    setSent(false);
    setWhatsappFallback('');
  };

  const validate = () => {
    const next = {};
    if (!order.name.trim() || order.name.trim().length < 2) {
      next.name = 'Please add your name so we know who is ordering.';
    }
    if (!normalizeIndianPhone(order.phone)) {
      next.phone = 'Enter a 10-digit mobile number (e.g. 98765 43210).';
    }
    if (order.date && order.date < earliestDate) {
      next.date = `This bake needs more notice. Choose ${earliestDate} or later.`;
    }
    if (!selectedProduct || selectedProduct.active === false || selectedProduct.availability === 'sold-out' || (order.variantId && !selectedProduct.variants?.some((variant) => variant.id === order.variantId))) {
      next.itemId = 'This item is currently unavailable.';
    }
    if (order.fulfillment === 'delivery') {
      if (order.area.trim().length < 2) next.area = 'Enter your delivery area.';
      if (order.address.trim().length < 8) next.address = 'Add a complete delivery address.';
    }
    return next;
  };

  const message = useMemo(() => buildSingleOrderMessage({
    brandName: siteContent.brand.name,
    product: selectedOrderProduct,
    quantity,
    customer: { name: order.name, phone: order.phone },
    fulfillment: { method: order.fulfillment, area: order.area, address: order.address },
    neededBy: order.date,
    preferredTime: order.time,
    eggless: order.eggless,
    notes: order.notes,
    pickupMessage: siteContent.ordering.pickupMessage
  }), [order, quantity, selectedOrderProduct]);

  const submitOrder = (event) => {
    event.preventDefault();
    const next = validate();
    setErrors(next);
    const firstError = Object.keys(next).find((k) => next[k]);
    if (firstError) {
      formRef.current?.querySelector(`[name="${firstError}"]`)?.focus();
      return;
    }
    setEnquiryStatus('Saving your enquiry…');
    void saveOrderEnquiry({
        customer_name: order.name.trim().slice(0, 120),
        customer_phone: normalizeIndianPhone(order.phone),
        summary: message.slice(0, 2000),
        order_items: [{
          product_id: selectedProduct.id,
          product_name: selectedOrderProduct.name,
          variant_id: order.variantId || null,
          quantity,
          unit_price: selectedOrderProduct.price,
          price_on_request: Boolean(selectedOrderProduct.priceOnRequest)
        }],
        fulfilment: {
          method: order.fulfillment,
          area: order.area.trim(),
          address: order.address.trim(),
          preferred_time: order.time,
          eggless: order.eggless
        },
        requested_for: order.date || null,
        notes: order.notes.trim().slice(0, 2000),
        source: 'storefront'
      }).then((result) => setEnquiryStatus(result.saved
        ? 'Enquiry saved. Send your WhatsApp message so we can confirm your order.'
        : 'Please send the WhatsApp message to complete your enquiry.'));
    const url = buildWhatsAppUrl(siteContent.contact.whatsappNumber, message);
    const opened = window.open(url, '_blank');
    if (opened) {
      opened.opener = null;
      setWhatsappFallback('');
      setSent(true);
    } else {
      setWhatsappFallback(url);
      setSent(false);
    }
  };

  const quickOrderUrl = buildWhatsAppUrl(
    siteContent.contact.whatsappNumber,
    siteContent.ordering.quickOrderMessage
  );

  return (
    <section id="order" className="order" ref={ref}>
      <div className={`order-container ${inView ? 'visible' : ''}`}>
        <div className="order-left">
          <div className="section-tag">{content.eyebrow}</div>
          <h2 className="section-title order-title">{content.title}</h2>
          <p className="order-desc">{content.description}</p>

          <form className="online-order-form" onSubmit={submitOrder} ref={formRef} noValidate>
            <p className="order-form-intro">{content.formIntro}</p>
            <div className="form-row">
              <label htmlFor="order-name">
                <span>Name <em aria-hidden="true">*</em></span>
                <input
                  id="order-name"
                  name="name"
                  value={order.name}
                  onChange={updateOrder}
                  placeholder="Your name"
                  autoComplete="name"
                  required
                  minLength="2"
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={errors.name ? 'order-name-error' : undefined}
                  className={errors.name ? 'has-error' : ''}
                />
                {errors.name && <small id="order-name-error" className="field-error" role="alert">{errors.name}</small>}
              </label>
              <label htmlFor="order-phone">
                <span>Phone <em aria-hidden="true">*</em></span>
                <input
                  id="order-phone"
                  type="tel"
                  inputMode="numeric"
                  name="phone"
                  value={order.phone}
                  onChange={updateOrder}
                  placeholder="98765 43210"
                  autoComplete="tel"
                  required
                  maxLength="16"
                  aria-invalid={Boolean(errors.phone)}
                  aria-describedby={errors.phone ? 'order-phone-error' : 'order-phone-hint'}
                  className={errors.phone ? 'has-error' : ''}
                />
                <small id="order-phone-hint" className="field-hint">We use this only to confirm your order.</small>
                {errors.phone && <small id="order-phone-error" className="field-error" role="alert">{errors.phone}</small>}
              </label>
            </div>
            <div className="form-row">
              <label htmlFor="order-item">
                <span>Item</span>
                <select
                  id="order-item"
                  name="itemId"
                  value={order.itemId}
                  onChange={updateOrder}
                  aria-invalid={Boolean(errors.itemId)}
                  aria-describedby={errors.itemId ? 'order-item-error' : undefined}
                  disabled={!orderableProducts.length}
                >
                  {orderableProducts.map((product) => (
                    <option key={product.id} value={String(product.id)}>
                      {product.name} — {product.priceLabel || formatINR(product.price)}
                    </option>
                  ))}
                </select>
                {errors.itemId && <small id="order-item-error" className="field-error" role="alert">{errors.itemId}</small>}
              </label>
              <div className="order-quantity-field">
                <span className="order-field-label">Quantity</span>
                <div className="qty-stepper" role="group" aria-label="Order quantity">
                  <button type="button" onClick={() => stepQty(-1)} aria-label="Decrease quantity" disabled={quantity <= 1}>−</button>
                  <span aria-live="polite">{quantity} {quantity === 1 ? 'item' : 'items'}</span>
                  <button type="button" onClick={() => stepQty(1)} aria-label="Increase quantity" disabled={quantity >= siteContent.ordering.maxQuantity}>+</button>
                </div>
              </div>
            </div>

            {selectedProduct?.variants.length > 0 && (
              <label htmlFor="order-variant">
                <span>Size / option</span>
                <select id="order-variant" name="variantId" value={order.variantId} onChange={updateOrder}>
                  <option value="">Standard - {selectedProduct.priceLabel || formatINR(selectedProduct.price)}</option>
                  {selectedProduct.variants.map((variant) => (
                    <option key={variant.id} value={variant.id}>
                      {variant.name} - {variant.priceOnRequest ? 'Price on request' : formatINR(variant.price)}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {selectedOrderProduct && (
              <div className="order-product-summary" aria-live="polite">
                <img src={selectedOrderProduct.image} alt="" aria-hidden="true" />
                <div>
                  <strong>{selectedOrderProduct.name}</strong>
                  <span>{selectedOrderProduct.priceLabel || formatINR(selectedOrderProduct.price)} · {selectedOrderProduct.leadTime}</span>
                </div>
              </div>
            )}

            <fieldset className="order-fulfillment">
              <legend>How would you like your order?</legend>
              <div className="order-segment">
                <label className={order.fulfillment === 'delivery' ? 'active' : ''}>
                  <input type="radio" name="fulfillment" value="delivery" checked={order.fulfillment === 'delivery'} onChange={updateOrder} />
                  <span>Delivery</span>
                </label>
                <label className={order.fulfillment === 'pickup' ? 'active' : ''}>
                  <input type="radio" name="fulfillment" value="pickup" checked={order.fulfillment === 'pickup'} onChange={updateOrder} />
                  <span>Pickup</span>
                </label>
              </div>
            </fieldset>

            {order.fulfillment === 'delivery' && (
              <>
                <label htmlFor="order-area">
                  <span>Delivery area <em aria-hidden="true">*</em></span>
                  <input
                    id="order-area"
                    name="area"
                    value={order.area}
                    onChange={updateOrder}
                    placeholder={siteContent.contact.deliveryAreas.slice(0, 2).join(', ') || 'Your area'}
                    required
                    aria-invalid={Boolean(errors.area)}
                    aria-describedby={errors.area ? 'order-area-error' : undefined}
                    className={errors.area ? 'has-error' : ''}
                  />
                  {errors.area && <small id="order-area-error" className="field-error" role="alert">{errors.area}</small>}
                </label>
                <label htmlFor="order-address">
                  <span>Full address <em aria-hidden="true">*</em></span>
                  <textarea
                    id="order-address"
                    name="address"
                    value={order.address}
                    onChange={updateOrder}
                    autoComplete="street-address"
                    placeholder="Flat, building, street and landmark"
                    maxLength="220"
                    required
                    aria-invalid={Boolean(errors.address)}
                    aria-describedby={errors.address ? 'order-address-error' : undefined}
                    className={errors.address ? 'has-error' : ''}
                  />
                  {errors.address && <small id="order-address-error" className="field-error" role="alert">{errors.address}</small>}
                </label>
              </>
            )}

            <div className="form-row">
              <label htmlFor="order-date">
                <span>Needed by</span>
                <input
                  id="order-date"
                  type="date"
                  name="date"
                  value={order.date}
                  min={earliestDate}
                  onChange={updateOrder}
                  aria-invalid={Boolean(errors.date)}
                  aria-describedby={errors.date ? 'order-date-error' : 'order-date-hint'}
                  className={errors.date ? 'has-error' : ''}
                />
                <small id="order-date-hint" className="field-hint">Optional if your date is flexible.</small>
                {errors.date && <small id="order-date-error" className="field-error" role="alert">{errors.date}</small>}
              </label>
              <label htmlFor="order-time">
                <span>Preferred time</span>
                <select id="order-time" name="time" value={order.time} onChange={updateOrder}>
                  {siteContent.ordering.timeSlots.map((slot) => <option key={slot}>{slot}</option>)}
                </select>
              </label>
            </div>

            <label className="order-checkbox">
              <input
                type="checkbox"
                name="eggless"
                checked={order.eggless}
                onChange={updateOrder}
                disabled={selectedProduct?.eggless === 'unavailable'}
              />
              <span>{selectedProduct?.eggless === 'unavailable' ? 'Eggless is unavailable for this bake' : siteContent.legal.egglessNotice}</span>
            </label>
            <label htmlFor="order-notes">
              <span>Message on cake / notes</span>
              <textarea
                id="order-notes"
                name="notes"
                value={order.notes}
                onChange={updateOrder}
                placeholder="Flavour, eggless preference, delivery area, custom text..."
                maxLength="300"
                aria-describedby="order-notes-count"
              />
              <small id="order-notes-count" className="field-hint field-count">{order.notes.length}/300</small>
            </label>

            <details className="wa-preview">
              <summary className="wa-preview-head">
                <Icon name="whatsapp" size={15} />
                <span>Review WhatsApp message</span>
              </summary>
              <pre>{message}</pre>
            </details>

            <button type="submit" className="order-submit">
              <Icon name="whatsapp" size={18} />
              <span>Continue on WhatsApp</span>
            </button>
            <p role="status" className="order-submit-note">{enquiryStatus}</p>
            <p className="order-submit-note">{siteContent.ordering.confirmationNote}</p>
            <p className="order-sent" role="status" aria-live="polite">
              {sent ? 'WhatsApp opened with your order details ready to send.' : ''}
            </p>
            {whatsappFallback && (
              <p className="order-whatsapp-fallback" role="alert">
                WhatsApp did not open.{' '}
                <a href={whatsappFallback} target="_blank" rel="noreferrer">Open your order directly.</a>
              </p>
            )}
            {studentOffer && (
              <p className="order-student-note">
                <strong>{studentOffer.title}: {studentOffer.valueLabel}</strong> · {studentOffer.terms}
              </p>
            )}
          </form>

          <div className="contact-cards">
            <a href={quickOrderUrl} target="_blank" rel="noreferrer" className="contact-card whatsapp">
              <span className="contact-icon"><Icon name="whatsapp" size={22} /></span>
              <div>
                <div className="contact-title">Quick WhatsApp</div>
                <div className="contact-sub">Ask for today's availability</div>
              </div>
              <span className="contact-arrow"><Icon name="arrow" size={18} strokeWidth={2} /></span>
            </a>
            <a href={`tel:+${siteContent.contact.whatsappNumber}`} className="contact-card phone">
              <span className="contact-icon"><Icon name="phone" size={22} /></span>
              <div>
                <div className="contact-title">Call Us</div>
                <div className="contact-sub">{siteContent.contact.phoneDisplay}</div>
              </div>
              <span className="contact-arrow"><Icon name="arrow" size={18} strokeWidth={2} /></span>
            </a>
            <a href={instagramUrl} target="_blank" rel="noreferrer" className="contact-card instagram">
              <span className="contact-icon"><Icon name="instagram" size={22} /></span>
              <div>
                <div className="contact-title">Instagram</div>
                <div className="contact-sub">{siteContent.contact.instagramHandle}</div>
              </div>
              <span className="contact-arrow"><Icon name="arrow" size={18} strokeWidth={2} /></span>
            </a>
          </div>
        </div>
        <div className="order-right">
          <ol className="order-steps">
            {content.steps.map((step, i) => (
              <li key={i}>
                <span className="step-num">{i + 1}</span>
                <div>
                  <strong>{step.title}</strong>
                  <span>{step.body}</span>
                </div>
              </li>
            ))}
          </ol>
          <div className="order-map">
            <iframe
              title={`${siteContent.brand.name} — ${siteContent.contact.pickupLabel}`}
              src={siteContent.contact.mapEmbedUrl}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="order-float-card">
              <img className="float-logo" src={siteContent.brand.logo} alt="" aria-hidden="true" />
              <div className="float-text">
                <strong>{content.mapTitle}</strong>
                <span>{content.mapSubtitle}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
export default Order;
